import { addMonths, isPeriod, toPeriod } from "../../../../../Utils/calendarDate";

export const NET_STATUS = {
  KURANG_BAYAR: "KURANG_BAYAR",
  LEBIH_BAYAR: "LEBIH_BAYAR",
  NIHIL: "NIHIL",
};

// Status is carried by the badge text; colour only repeats it. Underpaid is
// the normal outcome of a trading month, not a fault, so it is amber and not
// red.
const STATUS_VIEW = {
  KURANG_BAYAR: {
    badge: "reports.vatRecap.status.KURANG_BAYAR",
    label: "reports.vatRecap.summary.payable",
    badgeClass: "bg-amber-100 text-amber-800 ring-amber-600/20",
    cardClass: "bg-amber-50",
  },
  LEBIH_BAYAR: {
    badge: "reports.vatRecap.status.LEBIH_BAYAR",
    label: "reports.vatRecap.summary.overpaid",
    badgeClass: "bg-blue-50 text-blue-700 ring-blue-600/20",
    cardClass: "bg-blue-50/60",
  },
  NIHIL: {
    badge: "reports.vatRecap.status.NIHIL",
    label: "reports.vatRecap.summary.payable",
    badgeClass: "bg-gray-100 text-gray-700 ring-gray-500/20",
    cardClass: "bg-gray-50",
  },
};

/** The tax period a user most likely came to file: last month, "YYYY-MM". */
export const defaultPeriod = (now = new Date()) =>
  addMonths(toPeriod(now), -1);

export const periodFromParam = (value, now = new Date()) =>
  isPeriod(value) ? value : defaultPeriod(now);

/**
 * How the net figure reads: which badge and label it gets, and the amount to
 * show. The amount is always positive — "lebih bayar" already says which way
 * it goes, and a minus sign on a tax figure reads as an error.
 *
 * An unknown status falls back to the sign of `net.vat`, so a new server value
 * still renders a truthful badge rather than a blank one.
 */
export const netStatusView = (net) => {
  const vat = Number(net?.vat) || 0;
  let status = net?.status;
  if (!STATUS_VIEW[status]) {
    if (vat > 0) status = NET_STATUS.KURANG_BAYAR;
    else if (vat < 0) status = NET_STATUS.LEBIH_BAYAR;
    else status = NET_STATUS.NIHIL;
  }
  return { status, amount: Math.abs(vat), ...STATUS_VIEW[status] };
};

// Bills credited in another period are listed for context but belong to that
// period's return, so they stay out of this one's totals.
export const isCredited = (bill) => bill?.credited !== false;

const TOTAL_FIELDS = ["taxBase", "otherTaxBase", "vat", "luxuryTax"];

// Summed in sen so thirteen 2-decimal amounts do not drift into float noise.
export const sumRows = (rows = []) =>
  TOTAL_FIELDS.reduce((totals, field) => {
    const sen = rows.reduce(
      (sum, row) => sum + Math.round((Number(row?.[field]) || 0) * 100),
      0,
    );
    return { ...totals, [field]: sen / 100 };
  }, {});

export const creditedBillTotals = (bills = []) =>
  sumRows(bills.filter(isCredited));

export const sumAmounts = (rows = []) =>
  rows.reduce((sum, row) => sum + Math.round((Number(row?.amount) || 0) * 100), 0) /
  100;

// Only a non-zero difference is worth a banner; the server rounds to sen, so
// anything under half a sen is float residue.
export const ledgerDifferences = (ledger) =>
  ["output", "luxury", "input"]
    .map((key) => ({ key, difference: Number(ledger?.[key]?.difference) || 0 }))
    .filter(({ difference }) => Math.abs(difference) >= 0.005);

export const isEmptyRecap = (recap) =>
  !recap?.output?.invoices?.length &&
  !recap?.output?.journals?.length &&
  !recap?.input?.bills?.length &&
  !recap?.input?.journals?.length;

// Taxes left out of the recap first: they are the ones that change the figure.
export const sortReview = (review = []) =>
  [...review].sort((a, b) => Number(a.counted) - Number(b.counted));

/** "2026-09-21" → "21/09/2026" without a Date, so no zone can shift the day. */
export const formatRecapDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || "-";
};

export const accountLabel = (account) => {
  if (!account) return "-";
  if (typeof account === "string") return account;
  return [account.code, account.name].filter(Boolean).join(" ") || "-";
};
